import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, doc, addDoc, getDoc, getDocs, setDoc, Timestamp } from "firebase/firestore";
import { careInsuranceTarget, defaultWorkPattern } from "../utils/payroll";
import { WorkPatternEditor } from "./PayrollSettings";

// 給与計算のための社員マスタ。
// 賃金は他の従業員から見えてはいけないので、users とは別の payrollProfiles に分け、
// 本人・会社管理者・「給与明細の配布」権限を持つ人だけが読めるルールにしてある。
//
// 振込口座はあえて持たない。実際の振込はこのアプリでは行わないため、
// 口座番号を預かる意味がなく、漏洩リスクだけが増えるため。支給方法の区分だけ保持する。

export const EMPLOYMENT_TYPES = ["正社員", "契約社員", "パート・アルバイト", "役員"];
export const WAGE_TYPES       = ["月給", "日給", "時給"];
export const PAY_METHODS      = ["振込", "手渡し", "その他"];
export const TAX_TABLES       = ["甲", "乙"];

export function emptyProfile(companyId) {
  return {
    companyId: companyId || "",
    employeeNo: "",
    department: "",
    employmentType: "正社員",
    gender: "",
    useCompanyPattern: true,      // 会社標準の所定労働時間に従う
    workPattern: null,            // 個別に設定する場合のみ使う
    birthDate: "",
    joinedOn: "",
    leftOn: "",
    wageType: "月給",
    wageAmount: 0,
    fixedOvertimeHours: 0,
    commuteAllowanceUnit: "月額",
    commuteAllowance: 0,
    payMethod: "振込",
    payMethodNote: "",
    healthInsurance: true,
    careInsurance: false,
    pensionInsurance: true,
    employmentInsurance: true,
    standardMonthlyPay: 0,
    dependents: 0,
    taxTable: "甲",
    residentTax: 0,
    note: "",
  };
}

const yen = (n) => `${Number(n || 0).toLocaleString("ja-JP")}円`;

const FIELD_LABELS = {
  employeeNo: "社員番号（会社独自）",
  department: "部署",
  employmentType: "雇用形態",
  gender: "性別",
  useCompanyPattern: "所定労働時間の設定方法",
  birthDate: "生年月日",
  joinedOn: "入社日",
  leftOn: "退職日",
  wageType: "賃金区分",
  wageAmount: "金額",
  fixedOvertimeHours: "みなし残業（時間）",
  commuteAllowanceUnit: "通勤手当の単位",
  commuteAllowance: "通勤手当",
  payMethod: "支給方法",
  payMethodNote: "支給方法の補足",
  healthInsurance: "健康保険",
  careInsurance: "介護保険",
  pensionInsurance: "厚生年金",
  employmentInsurance: "雇用保険",
  standardMonthlyPay: "標準報酬月額",
  dependents: "扶養親族等の数",
  taxTable: "税額表",
  residentTax: "住民税",
  note: "備考",
};

const MONEY_FIELDS = ["wageAmount", "commuteAllowance", "standardMonthlyPay", "residentTax"];

function showValue(key, v) {
  if (key === "useCompanyPattern") return v ? "会社の標準に従う" : "個別に設定";
  if (key === "workPattern") return v ? "設定あり" : "（未設定）";
  if (typeof v === "boolean") return v ? "加入" : "未加入";
  if (v === "" || v === null || v === undefined) return "（未入力）";
  if (MONEY_FIELDS.includes(key)) return yen(v);
  return String(v);
}

/** 変更された項目だけを {field, label, before, after} の配列で返す */
function diffProfile(before, after) {
  return Object.keys(FIELD_LABELS)
    .filter(k => showValue(k, before?.[k]) !== showValue(k, after?.[k]))
    .map(k => ({
      field: k,
      label: FIELD_LABELS[k],
      before: showValue(k, before?.[k]),
      after: showValue(k, after?.[k]),
    }));
}

const currentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtDateTime = (ts) => ts?.toDate
  ? ts.toDate().toLocaleString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
  : "";

// ============================================================
// 給与情報の編集モーダル
// ============================================================
function PayrollProfileModal({ targetUser, companyId, editorEmail, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [original, setOriginal] = useState(null);   // 差分を取るための保存時点の値
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);     // null = 未取得

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "payrollProfiles", targetUser.id));
        const data = snap.exists()
          ? { ...emptyProfile(companyId), ...snap.data() }
          : emptyProfile(companyId);
        if (alive) { setP(data); setOriginal(snap.exists() ? data : null); }
      } catch (e) {
        console.error("給与情報の取得に失敗しました:", e);
        if (alive) { setP(emptyProfile(companyId)); setOriginal(null); setError("既存データの読み込みに失敗しました。"); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [targetUser.id, companyId]);

  const set    = (k, v) => setP(prev => ({ ...prev, [k]: v }));
  const setNum = (k, v) => setP(prev => ({ ...prev, [k]: v === "" ? 0 : Number(v) }));

  const handleSave = async () => {
    setError("");
    if (p.leftOn && p.joinedOn && p.leftOn < p.joinedOn) {
      setError("退職日が入社日より前になっています。");
      return;
    }
    const changes = diffProfile(original, p);
    if (original && changes.length === 0) {
      setError("変更されている項目がありません。");
      return;
    }

    setSaving(true);
    try {
      const now = Timestamp.now();
      const saved = {
        ...p,
        companyId: companyId || "",
        uid: targetUser.id,
        name: targetUser.name || "",
        updatedAt: now,
        updatedBy: editorEmail || "",
      };
      // 本体は上書き保存。いつ誰が何を変えたかは history に積んでいく。
      await setDoc(doc(db, "payrollProfiles", targetUser.id), saved, { merge: true });
      try {
        await addDoc(collection(db, "payrollProfiles", targetUser.id, "history"), {
          companyId: companyId || "",
          uid: targetUser.id,
          changedAt: now,
          changedBy: editorEmail || "",
          isFirst: !original,
          changes,
          snapshot: p,
        });
      } catch (e) {
        // 履歴が残せなくても本体の保存は成立しているので、警告だけ出す
        console.warn("変更履歴の記録に失敗しました:", e);
      }
      onSaved(targetUser.id, saved);
      onClose();
    } catch (e) {
      console.error("給与情報の保存に失敗しました:", e);
      setError("保存に失敗しました。権限がないか、通信に失敗した可能性があります。");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    setShowHistory(true);
    if (history !== null) return;
    try {
      const snap = await getDocs(collection(db, "payrollProfiles", targetUser.id, "history"));
      setHistory(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.changedAt - a.changedAt)
      );
    } catch (e) {
      console.error("変更履歴の取得に失敗しました:", e);
      setHistory([]);
      setError("変更履歴を読み込めませんでした。");
    }
  };

  const chips = (options, value, onPick) => (
    <div style={st.chipRow}>
      {options.map(o => (
        <button key={o} type="button"
          style={{ ...st.chip, ...(value === o ? st.chipActive : {}) }}
          onClick={() => onPick(o)}
        >{o}</button>
      ))}
    </div>
  );

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>
          給与情報：{targetUser.name || "（氏名未登録）"}
          <span style={st.modalEmail}>{targetUser.email}</span>
        </h3>
        {targetUser.nameKana && <div style={st.modalKana}>{targetUser.nameKana}</div>}
        {targetUser.memberNo && <div style={st.modalMemberNo}>社員ID：{targetUser.memberNo}</div>}
        {!loading && (
          <div style={st.metaBar}>
            <span>
              {original?.updatedAt
                ? `最終更新：${fmtDateTime(original.updatedAt)}${original.updatedBy ? ` · ${original.updatedBy}` : ""}`
                : "未登録（まだ保存されていません）"}
            </span>
            <button type="button" style={st.historyBtn}
              onClick={() => (showHistory ? setShowHistory(false) : loadHistory())}>
              {showHistory ? "入力に戻る" : "変更履歴"}
            </button>
          </div>
        )}

        {loading ? <p style={{ color: "#999" }}>読み込み中...</p> : showHistory ? (
          <>
            <div style={st.section}>変更履歴</div>
            {history === null
              ? <p style={{ color: "#999", fontSize: "14px" }}>読み込み中...</p>
              : history.length === 0
                ? <p style={{ color: "#999", fontSize: "14px" }}>履歴はまだありません。</p>
                : history.map(h => (
                  <div key={h.id} style={st.histCard}>
                    <div style={st.histHead}>
                      <span style={st.histDate}>{fmtDateTime(h.changedAt)}</span>
                      <span style={st.histWho}>{h.changedBy || "－"}</span>
                    </div>
                    {h.isFirst && <div style={st.histFirst}>新規登録</div>}
                    {(h.changes || []).map((c, i) => (
                      <div key={i} style={st.histRow}>
                        <span style={st.histLabel}>{c.label}</span>
                        <span style={st.histBefore}>{c.before}</span>
                        <span style={st.histArrow}>→</span>
                        <span style={st.histAfter}>{c.after}</span>
                      </div>
                    ))}
                  </div>
                ))
            }
            <div style={st.btnRow}>
              <button style={st.cancelBtn} onClick={() => setShowHistory(false)}>入力に戻る</button>
            </div>
          </>
        ) : (
          <>
            <div style={st.section}>基本情報</div>
            <div style={st.row2}>
              <div>
                <label style={st.label}>社員番号 <span style={st.opt}>（会社独自の番号）</span></label>
                <input style={st.input} value={p.employeeNo} onChange={e => set("employeeNo", e.target.value)} placeholder="例：0012" />
              </div>
              <div>
                <label style={st.label}>部署</label>
                <input style={st.input} value={p.department} onChange={e => set("department", e.target.value)} placeholder="例：営業部" />
              </div>
            </div>

            <label style={st.label}>雇用形態</label>
            {chips(EMPLOYMENT_TYPES, p.employmentType, v => set("employmentType", v))}

            <label style={st.label}>性別 <span style={st.opt}>（賃金台帳の記載事項）</span></label>
            {chips(["男", "女", "その他"], p.gender, v => set("gender", v))}

            <div style={st.row2}>
              <div>
                <label style={st.label}>生年月日 <span style={st.opt}>（介護保険の判定に使用）</span></label>
                <input style={st.input} type="date" value={p.birthDate} onChange={e => set("birthDate", e.target.value)} />
              </div>
              <div />
            </div>

            <div style={st.row2}>
              <div>
                <label style={st.label}>入社日</label>
                <input style={st.input} type="date" value={p.joinedOn} onChange={e => set("joinedOn", e.target.value)} />
              </div>
              <div>
                <label style={st.label}>退職日 <span style={st.opt}>（在籍中は空欄）</span></label>
                <input style={st.input} type="date" value={p.leftOn} onChange={e => set("leftOn", e.target.value)} />
              </div>
            </div>

            <div style={st.section}>所定労働時間</div>
            <div style={st.chipRow}>
              {[{ v: true, l: "会社の標準に従う" }, { v: false, l: "個別に設定する" }].map(o => (
                <button key={String(o.v)} type="button"
                  style={{ ...st.chip, ...(!!p.useCompanyPattern === o.v ? st.chipActive : {}) }}
                  onClick={() => setP(prev => ({
                    ...prev,
                    useCompanyPattern: o.v,
                    workPattern: o.v ? prev.workPattern : (prev.workPattern || defaultWorkPattern()),
                  }))}
                >{o.l}</button>
              ))}
            </div>
            {!p.useCompanyPattern && (
              <WorkPatternEditor pattern={p.workPattern || defaultWorkPattern()}
                onChange={v => set("workPattern", v)} />
            )}

            <div style={st.section}>賃金</div>
            <label style={st.label}>賃金区分</label>
            {chips(WAGE_TYPES, p.wageType, v => set("wageType", v))}

            <div style={st.row2}>
              <div>
                <label style={st.label}>金額（{p.wageType}）</label>
                <input style={st.input} type="number" min="0" value={p.wageAmount || ""} onChange={e => setNum("wageAmount", e.target.value)} placeholder="0" />
              </div>
              <div>
                <label style={st.label}>みなし残業 <span style={st.opt}>（時間／無ければ0）</span></label>
                <input style={st.input} type="number" min="0" value={p.fixedOvertimeHours || ""} onChange={e => setNum("fixedOvertimeHours", e.target.value)} placeholder="0" />
              </div>
            </div>

            <label style={st.label}>通勤手当</label>
            {chips(["月額", "日額"], p.commuteAllowanceUnit, v => set("commuteAllowanceUnit", v))}
            <input style={st.input} type="number" min="0" value={p.commuteAllowance || ""} onChange={e => setNum("commuteAllowance", e.target.value)} placeholder="0" />

            <div style={st.section}>支給方法</div>
            {chips(PAY_METHODS, p.payMethod, v => set("payMethod", v))}
            {p.payMethod === "その他" && (
              <input style={st.input} value={p.payMethodNote} onChange={e => set("payMethodNote", e.target.value)} placeholder="支給方法の補足" />
            )}
            <p style={st.hint}>振込口座はこのアプリでは預かりません。区分のみ記録します。</p>

            <div style={st.section}>社会保険・税</div>
            <div style={st.checkRow}>
              <label style={st.check}>
                <input type="checkbox" checked={!!p.healthInsurance} onChange={e => set("healthInsurance", e.target.checked)} style={st.checkbox} />健康保険
              </label>
              {p.birthDate ? (
                <span style={st.autoCare}>
                  介護保険：{careInsuranceTarget(p.birthDate, currentYearMonth()) ? "対象（自動判定）" : "対象外（自動判定）"}
                </span>
              ) : (
                <label style={st.check}>
                  <input type="checkbox" checked={!!p.careInsurance} onChange={e => set("careInsurance", e.target.checked)} style={st.checkbox} />介護保険（40歳以上）
                </label>
              )}
              <label style={st.check}>
                <input type="checkbox" checked={!!p.pensionInsurance} onChange={e => set("pensionInsurance", e.target.checked)} style={st.checkbox} />厚生年金
              </label>
              <label style={st.check}>
                <input type="checkbox" checked={!!p.employmentInsurance} onChange={e => set("employmentInsurance", e.target.checked)} style={st.checkbox} />雇用保険
              </label>
            </div>

            <div style={st.row2}>
              <div>
                <label style={st.label}>標準報酬月額</label>
                <input style={st.input} type="number" min="0" value={p.standardMonthlyPay || ""} onChange={e => setNum("standardMonthlyPay", e.target.value)} placeholder="0" />
              </div>
              <div>
                <label style={st.label}>扶養親族等の数</label>
                <input style={st.input} type="number" min="0" value={p.dependents || ""} onChange={e => setNum("dependents", e.target.value)} placeholder="0" />
              </div>
            </div>

            <div style={st.row2}>
              <div>
                <label style={st.label}>税額表 <span style={st.opt}>（扶養控除等申告書の提出有無）</span></label>
                {chips(TAX_TABLES, p.taxTable, v => set("taxTable", v))}
              </div>
              <div>
                <label style={st.label}>住民税 <span style={st.opt}>（月額）</span></label>
                <input style={st.input} type="number" min="0" value={p.residentTax || ""} onChange={e => setNum("residentTax", e.target.value)} placeholder="0" />
              </div>
            </div>

            <div style={st.section}>備考</div>
            <textarea style={{ ...st.input, height: "70px", resize: "vertical" }} value={p.note} onChange={e => set("note", e.target.value)} placeholder="特記事項があれば" />

            {error && <p style={st.error}>{error}</p>}

            <div style={st.btnRow}>
              <button style={st.saveBtn} onClick={handleSave} disabled={saving}>{saving ? "保存中..." : "保存"}</button>
              <button style={st.cancelBtn} onClick={onClose} disabled={saving}>キャンセル</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const st = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "24px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "560px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 8px", fontSize: "18px" },
  modalEmail: { marginLeft: "10px", fontSize: "13px", fontWeight: "normal", color: "#7f8c8d", wordBreak: "break-all" },
  modalMemberNo: { fontSize: "12px", color: "#2980b9", fontFamily: "monospace", marginBottom: "8px" },
  modalKana: { fontSize: "12px", color: "#7f8c8d", marginBottom: "2px" },
  section: { color: "#2c3e50", fontWeight: "bold", fontSize: "14px", margin: "22px 0 12px", paddingBottom: "6px", borderBottom: "2px solid #f0f4f8" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "13px", fontWeight: "bold" },
  opt: { fontWeight: "normal", color: "#999", fontSize: "11px" },
  input: { width: "100%", padding: "10px 12px", marginBottom: "14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" },
  chipRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" },
  chip: { padding: "7px 14px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  chipActive: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  checkRow: { display: "flex", gap: "18px", flexWrap: "wrap", marginBottom: "14px" },
  check: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", cursor: "pointer" },
  checkbox: { marginRight: "8px", width: "17px", height: "17px" },
  hint: { fontSize: "12px", color: "#95a5a6", margin: "-6px 0 0" },
  autoCare: { display: "inline-flex", alignItems: "center", fontSize: "14px", color: "#2980b9", backgroundColor: "#eaf3fa", borderRadius: "8px", padding: "6px 12px" },
  error: { color: "#e74c3c", fontSize: "13px", marginTop: "12px" },
  btnRow: { display: "flex", gap: "12px", marginTop: "24px" },
  metaBar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "12px", color: "#7f8c8d", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "8px 12px", marginBottom: "4px" },
  historyBtn: { padding: "5px 14px", backgroundColor: "#fff", color: "#2980b9", border: "1px solid #2980b9", borderRadius: "6px", cursor: "pointer", fontSize: "12px", flexShrink: 0 },
  histCard: { border: "1px solid #e6ebf0", borderRadius: "10px", padding: "14px 16px", marginBottom: "10px", backgroundColor: "#fafbfc" },
  histHead: { display: "flex", justifyContent: "space-between", gap: "10px", marginBottom: "8px", flexWrap: "wrap" },
  histDate: { fontSize: "13px", fontWeight: "bold", color: "#2c3e50" },
  histWho: { fontSize: "12px", color: "#7f8c8d" },
  histFirst: { display: "inline-block", fontSize: "11px", fontWeight: "bold", color: "#fff", backgroundColor: "#27ae60", borderRadius: "10px", padding: "2px 10px", marginBottom: "8px" },
  histRow: { display: "flex", alignItems: "baseline", gap: "8px", fontSize: "13px", padding: "3px 0", flexWrap: "wrap" },
  histLabel: { color: "#7f8c8d", minWidth: "120px" },
  histBefore: { color: "#95a5a6", textDecoration: "line-through" },
  histArrow: { color: "#bbb" },
  histAfter: { color: "#2c3e50", fontWeight: "bold" },
  saveBtn: { padding: "10px 28px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  cancelBtn: { padding: "10px 24px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default PayrollProfileModal;
